import type { Theme } from "@mui/material";
import chroma from "chroma-js";
import { DateTime } from "luxon";

export const MAX_DATE = DateTime.fromISO("9999-12-31T00:00:00Z").startOf("day");

function interpolateColors(color1: string, color2: string, percentage: number) {
	return chroma.mix(color1, color2, percentage).hex();
}

export const calculateDateInfo = (date: string, variant: "fromDate" | "toDate", theme: Theme) => {
	const parsedDate = DateTime.fromISO(date, { locale: "cs" });

	if (!parsedDate.isValid) {
		return {
			color: theme.palette.error.main,
			timeLeftText: "Neplatné datum",
			formattedDate: null,
		};
	}

	if (parsedDate.year === MAX_DATE.year) {
		return {
			color: theme.palette.grey[600],
			timeLeftText: "Bez omezení konce platnosti",
			formattedDate: parsedDate,
		};
	}

	if (variant !== "toDate") {
		return {
			color: theme.palette.grey[600],
			timeLeftText: "",
			formattedDate: parsedDate,
		};
	}

	const publishToDateTime = parsedDate;
	const now = DateTime.now().setLocale("cs");
	const oneDayBefore = publishToDateTime.minus({ days: 1 });
	const oneWeekBefore = publishToDateTime.minus({ weeks: 1 });
	const twoWeeksBefore = publishToDateTime.minus({ weeks: 2 });

	let startColor: string;
	let endColor: string;
	let color = theme.palette.grey[600]; // Default color
	let timeLeftText = ""; // Text displaying how much time is left
	let percentage = 0;

	// More than two weeks before expiration
	if (now < twoWeeksBefore) {
		timeLeftText = ""; // Default message for more than two weeks
	}

	// Calculate days left for determining the text
	const daysLeft = Math.floor(publishToDateTime.diff(now, "days").days);

	// Two weeks to one week before expiration
	if (now >= twoWeeksBefore && now < oneWeekBefore) {
		startColor = theme.palette.grey[600];
		endColor = theme.palette.warning.main;
		const progress = now.diff(twoWeeksBefore).as("seconds");
		const total = oneWeekBefore.diff(twoWeeksBefore).as("seconds");
		percentage = progress / total;
		timeLeftText = `Konec aukce za ${daysLeft} ${daysLeft >= 5 ? "dnů" : daysLeft > 1 ? "dny" : "den"}`;
	}

	// One week to one day before expiration
	if (now >= oneWeekBefore && now < oneDayBefore) {
		startColor = theme.palette.warning.main;
		endColor = theme.palette.primary.main;
		const progress = now.diff(oneWeekBefore).as("seconds");
		const total = oneDayBefore.diff(oneWeekBefore).as("seconds");
		percentage = progress / total;
		timeLeftText = `Konec aukce za ${daysLeft} ${daysLeft >= 5 ? "dnů" : daysLeft > 1 ? "dny" : "den"}`;
	}

	// Last day before expiration, including handling of the "tomorrow" case
	if (now >= oneDayBefore && now < publishToDateTime) {
		startColor = theme.palette.primary.main;
		endColor = theme.palette.error.main;
		const progress = now.diff(oneDayBefore).as("seconds");
		const total = publishToDateTime.diff(oneDayBefore).as("seconds");
		percentage = progress / total;
		if (daysLeft >= 1) {
			// If there's a whole day or more left
			timeLeftText = "Konec aukce zítra";
		} else {
			// If it's less than a day left
			const hoursLeft = Math.floor(publishToDateTime.diff(now, "hours").hours);
			const minutesLeft = Math.floor(publishToDateTime.diff(now, "minutes").minutes) % 60;
			const secondsLeft = Math.floor(publishToDateTime.diff(now, "seconds").seconds) % 60;
			timeLeftText = `Konec aukce za ${hoursLeft} h ${minutesLeft} m ${secondsLeft} s`;
		}
	}

	// @ts-expect-error
	if (startColor && endColor) {
		color = interpolateColors(startColor, endColor, percentage);
	}

	return {
		color: color,
		timeLeftText: timeLeftText,
		formattedDate: publishToDateTime,
	};
};
